/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, Sparkles, HandHeart } from 'lucide-react';
import ConfidenceChip from '@/components/terra/ConfidenceChip';
import MentorshipPairingCard from '@/components/terra/MentorshipPairingCard';

/**
 * Counselor Capability Map — explicitly not comparison culture.
 *
 * Surfaces "Strong at" and "Needs support at" rungs per counselor as
 * mentorship signals — never as performance rankings or competition.
 */
export default function CounselorCapabilityPage() {
  const [data, setData] = useState(null);
  const [pairings, setPairings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(180);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/terra-growth/intelligence/counselor-capability?days=${days}`),
      axios.get(`${API}/terra-growth/intelligence/mentorship-pairings?days=${days}`),
    ])
      .then(([r, p]) => { setData(r.data); setPairings(p.data); })
      .catch(() => { setData(null); setPairings(null); })
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="counselor-capability-page" className="max-w-7xl mx-auto px-6 py-8">
        <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Counselor Capability Map</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
              Growth capability patterns — not comparison culture. Strong at one rung is a mentorship signal for another.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[90, 180, 365].map(d => (
              <button key={d} data-testid={`cap-days-${d}`} onClick={() => setDays(d)}
                      className={`text-[11px] rounded-full px-3 py-1 border ${
                        days === d ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'}`}>{d}d</button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Computing…
          </div>
        ) : !data || data.counselors.length === 0 ? (
          <div data-testid="counselor-capability-empty" className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            No transitions in this window. Capability map will form over time.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.counselors.map(c => (
                <CounselorCard key={c.user_id} c={c} labels={data.perception_labels || {}} />
              ))}
            </div>
            {pairings && pairings.pairings.length > 0 && (
              <section data-testid="capability-pairings">
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#8C6239] mb-2">
                  Suggested Mentorship Pairings™
                </p>
                <p className="text-[11px] text-[#8A8681] italic mb-3 max-w-3xl">
                  {pairings.framing_note}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pairings.pairings.slice(0, 8).map((p, i) => (
                    <MentorshipPairingCard key={i} pairing={p}
                                            labels={data.perception_labels || {}} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function CounselorCard({ c, labels }) {
  return (
    <article data-testid={`capability-card-${c.user_id}`}
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5 space-y-3">
      <header className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-[#2C2A29] truncate">{c.name}</p>
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">{c.role}</p>
        </div>
        <ConfidenceChip code={c.confidence} sample={c.total_transitions} compact />
      </header>

      <Section
        testId={`strong-at-${c.user_id}`}
        icon={<Sparkles className="w-3 h-3 text-[#4A5D4E]" />}
        title="Strong at"
        empty="No reliable strengths surfaced yet."
        edges={c.strong_at} labels={labels} tone="emerald"
      />

      <Section
        testId={`needs-support-${c.user_id}`}
        icon={<HandHeart className="w-3 h-3 text-[#8C6239]" />}
        title="Needs support at"
        empty="No support pattern surfacing — capability map is still forming."
        edges={c.needs_support_at} labels={labels} tone="amber"
        helpNote="Mentorship signal. Pair this counselor with a strong colleague at this rung."
      />
    </article>
  );
}

function Section({ testId, icon, title, edges, empty, labels, tone, helpNote }) {
  const toneClass = tone === 'emerald'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-amber-50 text-amber-800 border-amber-200';
  return (
    <section data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] flex items-center gap-1">
        {icon} {title}
      </p>
      {edges.length === 0 ? (
        <p className="text-[11px] text-[#B5B0A8] mt-1">{empty}</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {edges.map(e => (
            <li key={e.from_state} className="flex items-center justify-between gap-2">
              <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${toneClass}`}>
                {labels[e.from_state] || e.from_state}
              </span>
              <span className="text-[11px] tabular-nums text-[#5C5955]">{e.velocity_pct}% · {e.sample_count}</span>
            </li>
          ))}
        </ul>
      )}
      {helpNote && edges.length > 0 && (
        <p className="text-[10px] text-[#8A8681] italic mt-1.5">{helpNote}</p>
      )}
    </section>
  );
}
